import type { NavigationAccessInput, WorkspaceView } from '../components/workspaces/admin/navigationRegistry'

export interface CommandContext {
  workspace: WorkspaceView
  hasPermission: (permission: string) => boolean
  hasFeature: (feature: string) => boolean
  navigation?: { input: NavigationAccessInput; isSimple: boolean; route?: string }
}

/** Labels/descriptions/keywords are translation keys, resolved at presentation time. */
export interface CommandDefinition {
  id: string
  label: string
  description?: string
  keywords?: readonly string[]
  category: string
  workspaces?: readonly WorkspaceView[]
  requiredPermissions?: readonly string[] // all required; existing access helper resolves aliases
  featureEntitlement?: string
  available?: (context: CommandContext) => boolean
  execute: (context: CommandContext) => void | boolean | Promise<void | boolean>
  remember?: boolean // Opt in only static, non-sensitive identifiers.
  shortcut?: string // display metadata; only the palette shortcut is bound in Phase 2A
  destination?: { route: string; workspace: WorkspaceView }
}

export function commandAvailable(command: CommandDefinition, context: CommandContext): boolean {
  return (!command.workspaces || command.workspaces.includes(context.workspace))
    && (command.requiredPermissions || []).every(context.hasPermission)
    && (!command.featureEntitlement || context.hasFeature(command.featureEntitlement))
    && (command.available?.(context) ?? true)
}

/** A scope owns this store. Retained callbacks must pass its live context guard. */
export function createCommandRegistry(getContext: () => CommandContext | null,
  onSuccess?: (command: CommandDefinition) => void) {
  let snapshot: readonly CommandDefinition[] = []
  const listeners = new Set<() => void>()
  const publish = () => listeners.forEach(listener => listener())
  return {
    getSnapshot: () => snapshot,
    subscribe: (listener: () => void) => {
      listeners.add(listener)
      return () => { listeners.delete(listener) }
    },
    register: (commands: readonly CommandDefinition[]) => {
      const ids = new Set(snapshot.map(command => command.id))
      for (const command of commands) {
        if (!command.id.trim() || ids.has(command.id)) throw new Error(`Duplicate or empty command ID: ${command.id}`)
        ids.add(command.id)
      }
      // Owned copies make cleanup/execute resistant to later caller mutation.
      const owned: readonly CommandDefinition[] = commands.map(command => Object.freeze({ ...command,
        keywords: command.keywords && Object.freeze([...command.keywords]),
        workspaces: command.workspaces && Object.freeze([...command.workspaces]),
        requiredPermissions: command.requiredPermissions && Object.freeze([...command.requiredPermissions]),
      }))
      snapshot = [...snapshot, ...owned]
      publish()
      return () => {
        snapshot = snapshot.filter(command => !owned.includes(command))
        publish()
      }
    },
    execute: (command: CommandDefinition, beforeExecute?: () => void): boolean | Promise<boolean> => {
      let context = getContext()
      if (!context || !snapshot.includes(command) || !commandAvailable(command, context)) return false
      beforeExecute?.()
      context = getContext()
      if (!context || !snapshot.includes(command) || !commandAvailable(command, context)) return false
      const complete = (result: void | boolean) => {
        const current = getContext()
        if (result === false || !current || !snapshot.includes(command) || !commandAvailable(command, current)) return false
        if (command.remember) onSuccess?.(command)
        return true
      }
      const result = command.execute(context)
      return result instanceof Promise ? result.then(complete) : complete(result)
    },
  }
}

export type CommandRegistry = ReturnType<typeof createCommandRegistry>
export interface CommandResult { command: CommandDefinition; label: string; description?: string; category: string; recentlyUsed?: boolean }

/** Pure local search. Exact label, prefix, label substring, then keywords/description. */
export function searchCommands(commands: readonly CommandDefinition[], context: CommandContext,
  query: string, t: (key: string) => string, recentIds: readonly string[] = []): CommandResult[] {
  const needle = query.trim().toLocaleLowerCase()
  return commands.filter(command => commandAvailable(command, context)).map(command => {
    const label = t(command.label), description = command.description ? t(command.description) : undefined
    const normalized = label.toLocaleLowerCase()
    const other = [command.label, ...(command.keywords || []).flatMap(key => [key, t(key)]), description || ''].join(' ').toLocaleLowerCase()
    const score = !needle ? 0 : normalized === needle ? 4 : normalized.startsWith(needle) ? 3
      : normalized.includes(needle) ? 2 : other.includes(needle) ? 1 : -1
    const recent = command.remember ? recentIds.indexOf(command.id) : -1
    return { command, label, description, category: t(command.category), score, recentlyUsed: recent >= 0, recentRank: recent < 0 ? Number.MAX_SAFE_INTEGER : recent }
  }).filter(result => result.score >= 0).sort((a, b) => b.score - a.score
    || a.recentRank - b.recentRank
    || a.category.localeCompare(b.category) || a.label.localeCompare(b.label) || a.command.id.localeCompare(b.command.id))
}
